import { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Button, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { MIconButton } from '@/@material-extend';
import { useSnackbar } from 'notistack';

import { NOTE_ACTIONS, StaffNote, StaffNoteComment } from './Types';
import { formDataInstance } from '@/common/axios';
import CONFIG from '@/config';
import { getRestHeaders } from '@/common/utils/api';
import { getErrorDetails } from './ApiErrorLookup';

const useStyles: any = makeStyles(() => ({
  root: {},
  label: {
    fontSize: 12,
    fontWeight: 400,
    color: '#000'
  },
  saveCancel: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 8,
    '& button': {
      backgroundColor: '#055577',
      color: '#FFFFFF',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: '#042E43'
      }
    },
    '& button:first-child': {
      marginRight: 8,
      background: '#FAFAFA',
      border: 'none',
      color: '#000',
      '&:hover': {
        backgroundColor: '#FAFAFA'
      }
    }
  }
}));

export interface AddNoteCommentApiResponse {
  data: {
    data: {
      comment: StaffNoteComment;
      success?: boolean;
    };
  };
}

export interface NoteCommentFormProps {
  note: StaffNote;
  actionCallback: (
    note: StaffNote,
    action: keyof typeof NOTE_ACTIONS,
    data: any
  ) => void;
  hideForm: () => void;
}

export const apiAddNoteCommentSuffix = '/staff/user-note-comment';

export const NewCommentForm = (props: NoteCommentFormProps) => {
  const { note, actionCallback, hideForm } = props;

  const classes = useStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [nextComment, setNextComment] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const addComment = () => {
    setIsSaving(true);

    const params = {
      parentNoteId: note.id,
      content: nextComment
    };

    formDataInstance
      .post(`${CONFIG.USERS_API_URL}${apiAddNoteCommentSuffix}`, params, {
        headers: getRestHeaders()
      })
      .then((response: AddNoteCommentApiResponse) => {
        if (response.data.data.success) {
          enqueueSnackbar('Comment Added', {
            variant: 'success',
            action: (key: any) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)} />
            )
          });
          // update the record in the parent component
          actionCallback(
            note,
            NOTE_ACTIONS.INSERT_COMMENT,
            response.data.data.comment
          );
          hideForm();
          setNextComment('');
        }
      })
      .catch(error => {
        enqueueSnackbar(
          getErrorDetails('There was a problem adding user.', error),
          {
            variant: 'error',
            action: (key: any) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)} />
            )
          }
        );
      })
      .finally(() => setIsSaving(false));
  };

  const handleKeyDown = e => {
    if (e.key === 'Escape') {
      hideForm();
    }
  };

  return (
    <div className={classes.root}>
      <span className={classes.label}>Reply</span>
      <TextField
        fullWidth
        multiline
        rows={3}
        inputRef={input => input && input.focus()}
        onFocus={e =>
          e.currentTarget.setSelectionRange(
            e.currentTarget.value.length,
            e.currentTarget.value.length
          )
        }
        name="comment"
        value={nextComment}
        onChange={e => setNextComment(e.target.value)}
        onKeyDown={handleKeyDown}
        inputProps={{ maxLength: 4096 }}
      />
      <div className={classes.saveCancel} onClick={() => {}}>
        <Button variant="outlined" onClick={hideForm}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={addComment}
          loading={isSaving}
          disabled={nextComment.length === 0}
        >
          Submit
        </LoadingButton>
      </div>
    </div>
  );
};
